import axios, { CancelTokenSource } from "axios";

import { API } from "utils/api-config";

import { defaultErrorResponse } from "api/defaults";

import {
    Mailbox,
    MailboxDefault,
} from "store/environments/tenants/mailboxes/mailboxes-types";
import { Tenant } from "store/environments/environment-types";
import {
    OperationErrorLog,
    OperationHistory,
} from "store/common/operation-types";
import { MailboxesWorkflow } from "store/workflows/workflows-types";

export const createMailboxes = async (
    environmentId: string,
    tenantId: string,
    payload: Tenant<ReadonlyArray<Mailbox>>,
    cancelTokenSource?: CancelTokenSource
): Promise<[MailboxesWorkflow | null, number]> => {
    const response = await API.post(
        `environments/${environmentId}/tenants/${tenantId}/mailboxes/create`,
        payload,
        { cancelToken: cancelTokenSource?.token }
    );

    if (!cancelTokenSource?.token.reason) {
        if (!response) {
            return defaultErrorResponse;
        }

        return [response.data, response.status];
    } else {
        throw new axios.Cancel();
    }
};

export const updateMailboxes = async (
    environmentId: string,
    tenantId: string,
    payload: Tenant<ReadonlyArray<Mailbox>>,
    cancelTokenSource?: CancelTokenSource
): Promise<[MailboxesWorkflow | null, number]> => {
    const response = await API.post(
        `environments/${environmentId}/tenants/${tenantId}/mailboxes/update`,
        payload,
        { cancelToken: cancelTokenSource?.token }
    );

    if (!cancelTokenSource?.token.reason) {
        if (!response) {
            return defaultErrorResponse;
        }

        return [response.data, response.status];
    } else {
        throw new axios.Cancel();
    }
};

export const loadMailboxesWorkflows = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[MailboxesWorkflow[] | null, number]> => {
    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/mailboxes/workflows`,
        { cancelToken: cancelTokenSource?.token }
    );

    if (!cancelTokenSource?.token.reason) {
        if (!response) {
            return defaultErrorResponse;
        }

        return [response.data, response.status];
    } else {
        throw new axios.Cancel();
    }
};

export const loadMailboxes = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<Mailbox>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/mailboxes`,
        { cancelToken: cancelTokenSource?.token }
    );

    if (!cancelTokenSource?.token.reason) {
        if (!response) {
            return defaultErrorResponse;
        }

        return [response.data, response.status];
    } else {
        throw new axios.Cancel();
    }
};

export const loadDefaultMailboxes = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<MailboxDefault>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/mailboxes/default`,
        { cancelToken: cancelTokenSource?.token }
    );

    if (!cancelTokenSource?.token.reason) {
        if (!response) {
            return defaultErrorResponse;
        }

        return [response.data, response.status];
    } else {
        throw new axios.Cancel();
    }
};

export const loadMailboxesHistory = async (
    environmentId: string,
    tenantId: string,
    mailboxId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[ReadonlyArray<OperationHistory<Mailbox>> | null, number]> => {
    const response = await API.get(
        `/environments/${environmentId}/tenants/${tenantId}/mailboxes/${mailboxId}/history`,
        { cancelToken: cancelTokenSource?.token }
    );

    if (!cancelTokenSource?.token.reason) {
        if (!response) {
            return defaultErrorResponse;
        }

        return [response.data, response.status];
    } else {
        throw new axios.Cancel();
    }
};

export const loadMailboxesErrors = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[ReadonlyArray<OperationErrorLog<Mailbox>> | null, number]> => {
    const response = await API.get(
        `/environments/${environmentId}/tenants/${tenantId}/mailboxes/errors`,
        { cancelToken: cancelTokenSource?.token }
    );

    if (!cancelTokenSource?.token.reason) {
        if (!response) {
            return defaultErrorResponse;
        }

        return [response.data, response.status];
    } else {
        throw new axios.Cancel();
    }
};
